import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  ThemeProvider,
  CssBaseline,
  Fade,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Chip
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Phone,
  Badge,
  Flag,
  LocationOn,
  Save,
  Edit,
  PhotoCamera,
  Security,
  AccountCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const UserProfile = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');

  // Check if user is logged in
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    middleName: '',
    familyName: '',
    email: '',
    phone: '',
    country: '',
    nationality: '',
    civilId: '',
    gender: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Civil ID image states
  const [civilIdFrontPreview, setCivilIdFrontPreview] = useState(null);
  const [civilIdBackPreview, setCivilIdBackPreview] = useState(null);
  const [newCivilIdFront, setNewCivilIdFront] = useState(null);
  const [newCivilIdBack, setNewCivilIdBack] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!storedToken || !storedUser) {
      navigate('/');
      return;
    }

    setToken(storedToken);
    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Set initial profile data
    setProfileData({
      firstName: userData.profile?.firstName || '',
      middleName: userData.profile?.middleName || '',
      familyName: userData.profile?.familyName || '',
      email: userData.email || '',
      phone: userData.profile?.phone || '',
      country: userData.profile?.country || '',
      nationality: userData.profile?.nationality || '',
      civilId: userData.profile?.civilId || '',
      gender: userData.profile?.gender || ''
    });

    // Set civil ID image previews
    if (userData.profile?.civilIdImages?.front) {
      setCivilIdFrontPreview(`https://dhahabjw.com/${userData.profile.civilIdImages.front}`);
    }
    if (userData.profile?.civilIdImages?.back) {
      setCivilIdBackPreview(`https://dhahabjw.com/${userData.profile.civilIdImages.back}`);
    }
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'civilIdFront') {
          setCivilIdFrontPreview(reader.result);
          setNewCivilIdFront(file);
        } else if (field === 'civilIdBack') {
          setCivilIdBackPreview(reader.result);
          setNewCivilIdBack(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      Object.keys(profileData).forEach(key => {
        formData.append(key, profileData[key]);
      });

      if (newCivilIdFront) formData.append('civilIdFront', newCivilIdFront);
      if (newCivilIdBack) formData.append('civilIdBack', newCivilIdBack);

      const response = await fetch('https://dhahabjw.com/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t?.profileUpdated || 'Profile updated successfully!');
        
        // Update local storage
        const updatedUser = { ...user, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || t?.updateFailed || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(t?.networkError || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t?.passwordMismatch || 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError(t?.passwordTooShort || 'Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://dhahabjw.com/api/user/change-password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t?.passwordChanged || 'Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || t?.passwordChangeFailed || 'Failed to change password');
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(t?.networkError || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          position: 'relative'
        }}
      >
        <AppHeader
          language={language}
          setLanguage={setLanguage}
          t={t}
        />

        <Container maxWidth="lg" sx={{ pt: { xs: 12, sm: 15 }, pb: 8 }}>
          <Fade in={true} timeout={800}>
            <Box>
              {/* Profile Header Card */}
              <Card
                sx={{
                  mb: 3,
                  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(30, 30, 30, 0.95))',
                  border: '1px solid rgba(248, 210, 92, 0.2)'
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: { xs: 60, sm: 80 },
                        height: { xs: 60, sm: 80 },
                        bgcolor: 'primary.main',
                        color: '#000'
                      }}
                    >
                      <Person sx={{ fontSize: { xs: 30, sm: 40 } }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {`${profileData.firstName} ${profileData.familyName}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {profileData.email}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={profileData.country || 'Kuwait'}
                          size="small"
                          icon={<LocationOn />}
                          sx={{ bgcolor: 'rgba(248, 210, 92, 0.1)', color: 'primary.main' }}
                        />
                        <Chip
                          label={profileData.nationality || 'Not specified'}
                          size="small"
                          icon={<Flag />}
                          sx={{ bgcolor: 'rgba(248, 210, 92, 0.1)', color: 'primary.main' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Main Content */}
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(30, 30, 30, 0.95))',
                  border: '1px solid rgba(248, 210, 92, 0.2)'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiTab-root': {
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          color: 'primary.main'
                        }
                      }
                    }}
                  >
                    <Tab icon={<AccountCircle />} label={t?.personalInfo || 'Personal Info'} />
                    <Tab icon={<Security />} label={t?.security || 'Security'} />
                    <Tab icon={<Badge />} label={t?.documents || 'Documents'} />
                  </Tabs>

                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                      </Alert>
                    )}

                    {success && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                      </Alert>
                    )}

                    {/* Tab 0: Personal Information */}
                    {tabValue === 0 && (
                      <Box component="form" onSubmit={handleProfileUpdate}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label={t?.firstName || 'First Name'}
                              value={profileData.firstName}
                              onChange={(e) => handleInputChange('firstName', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Person sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label={t?.middleName || 'Middle Name'}
                              value={profileData.middleName}
                              onChange={(e) => handleInputChange('middleName', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Person sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label={t?.familyName || 'Family Name'}
                              value={profileData.familyName}
                              onChange={(e) => handleInputChange('familyName', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Person sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label={t?.email || 'Email'}
                              value={profileData.email}
                              disabled
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Email sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                              helperText={t?.emailNotEditable || 'Email cannot be changed'}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label={t?.phone || 'Phone'}
                              value={profileData.phone}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Phone sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label={t?.nationality || 'Nationality'}
                              value={profileData.nationality}
                              onChange={(e) => handleInputChange('nationality', e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Flag sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label={t?.civilId || 'Civil ID'}
                              value={profileData.civilId}
                              disabled
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Badge sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                              helperText={t?.civilIdNotEditable || 'Civil ID cannot be changed'}
                            />
                          </Grid>
                        </Grid>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          disabled={loading}
                          startIcon={<Save />}
                          sx={{
                            mt: 3,
                            py: 1.5,
                            background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                            color: '#000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                            }
                          }}
                        >
                          {loading ? <CircularProgress size={24} /> : (t?.saveChanges || 'Save Changes')}
                        </Button>
                      </Box>
                    )}

                    {/* Tab 1: Security */}
                    {tabValue === 1 && (
                      <Box component="form" onSubmit={handlePasswordUpdate}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label={t?.currentPassword || 'Current Password'}
                              type={showPassword ? 'text' : 'password'}
                              value={passwordData.currentPassword}
                              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Lock sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => setShowPassword(!showPassword)}
                                      edge="end"
                                    >
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label={t?.newPassword || 'New Password'}
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordData.newPassword}
                              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Lock sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => setShowNewPassword(!showNewPassword)}
                                      edge="end"
                                    >
                                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label={t?.confirmPassword || 'Confirm New Password'}
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordData.confirmPassword}
                              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Lock sx={{ color: 'primary.main' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          disabled={loading}
                          startIcon={<Security />}
                          sx={{
                            mt: 3,
                            py: 1.5,
                            background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                            color: '#000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                            }
                          }}
                        >
                          {loading ? <CircularProgress size={24} /> : (t?.changePassword || 'Change Password')}
                        </Button>
                      </Box>
                    )}

                    {/* Tab 2: Documents */}
                    {tabValue === 2 && (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                              {t?.civilIdFront || 'Civil ID Front'}
                            </Typography>
                            {civilIdFrontPreview && (
                              <Box sx={{ mb: 2 }}>
                                <img
                                  src={civilIdFrontPreview}
                                  alt="Civil ID Front"
                                  style={{ width: '100%', borderRadius: 8, border: '1px solid rgba(255, 255, 255, 0.1)' }}
                                />
                              </Box>
                            )}
                            <input
                              accept="image/*"
                              type="file"
                              id="civilIdFront"
                              style={{ display: 'none' }}
                              onChange={(e) => handleImageChange('civilIdFront', e.target.files[0])}
                            />
                            <label htmlFor="civilIdFront">
                              <Button
                                variant="outlined"
                                component="span"
                                fullWidth
                                startIcon={<PhotoCamera />}
                                sx={{
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'rgba(248, 210, 92, 0.1)'
                                  }
                                }}
                              >
                                {t?.uploadNewImage || 'Upload New Image'}
                              </Button>
                            </label>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                              {t?.civilIdBack || 'Civil ID Back'}
                            </Typography>
                            {civilIdBackPreview && (
                              <Box sx={{ mb: 2 }}>
                                <img
                                  src={civilIdBackPreview}
                                  alt="Civil ID Back"
                                  style={{ width: '100%', borderRadius: 8, border: '1px solid rgba(255, 255, 255, 0.1)' }}
                                />
                              </Box>
                            )}
                            <input
                              accept="image/*"
                              type="file"
                              id="civilIdBack"
                              style={{ display: 'none' }}
                              onChange={(e) => handleImageChange('civilIdBack', e.target.files[0])}
                            />
                            <label htmlFor="civilIdBack">
                              <Button
                                variant="outlined"
                                component="span"
                                fullWidth
                                startIcon={<PhotoCamera />}
                                sx={{
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'rgba(248, 210, 92, 0.1)'
                                  }
                                }}
                              >
                                {t?.uploadNewImage || 'Upload New Image'}
                              </Button>
                            </label>
                          </Grid>
                        </Grid>

                        {(newCivilIdFront || newCivilIdBack) && (
                          <Button
                            onClick={handleProfileUpdate}
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            startIcon={<Save />}
                            sx={{
                              mt: 3,
                              py: 1.5,
                              background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                              color: '#000',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #F8D25C 0%, #F8D25C 100%)',
                              }
                            }}
                          >
                            {loading ? <CircularProgress size={24} /> : (t?.saveDocuments || 'Save Documents')}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>

        <AppFooter language={language} t={t} />
      </Box>
    </ThemeProvider>
  );
};

export default UserProfile;